import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@clerk/clerk-react";
import { ADHDTodoManager } from "../../lib/adhd-todo-manager";
import { notificationService, NotificationPermission } from "../../lib/notification-service";
import { supportedLanguages, getCurrentLanguageInfo, changeLanguage } from "../../lib/i18n";
import packageInfo from "../../../package.json";
import styles from "./SettingsPage.module.scss";

interface SettingsPageProps {
  onNavigateBack?: () => void;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ onNavigateBack }) => {
  const { t } = useTranslation(["settings", "common"]);
  const { signOut } = useAuth();
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>("default");
  const [notificationSound, setNotificationSound] = useState(true);
  const [textSize, setTextSize] = useState("medium");
  const [highContrast, setHighContrast] = useState(false);
  const [screenReader, setScreenReader] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguageInfo().code);
  const [platformInfo] = useState(notificationService.getPlatformInfo());

  useEffect(() => {
    // Initialize notification permission state
    setNotificationPermission(notificationService.getPermission());

    // Listen for permission changes
    const unsubscribe = notificationService.onPermissionChange(permission => {
      setNotificationPermission(permission);
    });

    // Load settings from localStorage
    const savedNotificationSound = localStorage.getItem("flowcus-notification-sound");
    if (savedNotificationSound !== null) {
      setNotificationSound(JSON.parse(savedNotificationSound));
    }

    const savedTextSize = localStorage.getItem("flowcus-text-size");
    if (savedTextSize) {
      setTextSize(savedTextSize);
      document.documentElement.style.fontSize = getTextSizeValue(savedTextSize);
    }

    const savedHighContrast = localStorage.getItem("flowcus-high-contrast");
    if (savedHighContrast !== null) {
      setHighContrast(JSON.parse(savedHighContrast));
      document.documentElement.setAttribute("data-high-contrast", savedHighContrast);
    }

    const savedScreenReader = localStorage.getItem("flowcus-screen-reader");
    if (savedScreenReader !== null) {
      setScreenReader(JSON.parse(savedScreenReader));
      document.documentElement.setAttribute("data-screen-reader", savedScreenReader);
    }

    return unsubscribe;
  }, []);

  const getTextSizeValue = (size: string): string => {
    switch (size) {
      case "small":
        return "14px";
      case "medium":
        return "16px";
      case "large":
        return "18px";
      case "extra-large":
        return "20px";
      default:
        return "16px";
    }
  };

  const handleNotificationToggle = async () => {
    if (notificationPermission === "granted") {
      // Can't revoke permission programmatically, just show instructions
      alert(t("settings:notifications.revoke_instructions"));
      return;
    }

    if (notificationPermission === "default") {
      try {
        const permission = await notificationService.requestPermission();
        setNotificationPermission(permission);

        if (permission === "granted") {
          // Show a test notification
          notificationService.testNotification();
        }
      } catch (error) {
        console.error("Failed to request notification permission:", error);
        alert(t("settings:notifications.request_failed"));
      }
    }
  };

  const handleTestNotification = async () => {
    if (notificationPermission === "granted") {
      await notificationService.testNotification();
    } else {
      alert(t("settings:notifications.enable_first"));
    }
  };

  const handleNotificationSoundChange = (enabled: boolean) => {
    setNotificationSound(enabled);
    localStorage.setItem("flowcus-notification-sound", JSON.stringify(enabled));
  };

  const handleTextSizeChange = (size: string) => {
    setTextSize(size);
    localStorage.setItem("flowcus-text-size", size);
    document.documentElement.style.fontSize = getTextSizeValue(size);
  };

  const handleHighContrastChange = (enabled: boolean) => {
    setHighContrast(enabled);
    localStorage.setItem("flowcus-high-contrast", JSON.stringify(enabled));
    document.documentElement.setAttribute("data-high-contrast", enabled.toString());
  };

  const handleScreenReaderChange = (enabled: boolean) => {
    setScreenReader(enabled);
    localStorage.setItem("flowcus-screen-reader", JSON.stringify(enabled));
    document.documentElement.setAttribute("data-screen-reader", enabled.toString());
  };

  const handleLanguageChange = async (languageCode: string) => {
    try {
      await changeLanguage(languageCode);
      setCurrentLanguage(languageCode);
    } catch (error) {
      console.error("Failed to change language:", error);
    }
  };

  const handleExportData = () => {
    try {
      const todoManager = new ADHDTodoManager();
      const todos = todoManager.getTodos();
      const projects = todoManager.getProjects();

      // Generate CSV content for todos
      const todoHeaders = [
        "ID",
        "Title",
        "Description",
        "Priority",
        "Status",
        "Created At",
        "Due Date",
        "Project ID",
        "Tags",
        "Estimated Minutes",
        "Notes",
      ];
      const todoRows = todos.map(todo => [
        todo.id,
        `"${(todo.title || "").replace(/"/g, '""')}"`, // Escape quotes in CSV
        `"${(todo.description || "").replace(/"/g, '""')}"`,
        todo.priority,
        todo.status,
        todo.created_at,
        todo.due_date || "",
        todo.project_id || "",
        `"${todo.tags.join(", ")}"`,
        todo.estimated_minutes || "",
        `"${(todo.notes || "").replace(/"/g, '""')}"`,
      ]);

      // Generate CSV content for projects
      const projectHeaders = [
        "ID",
        "Name",
        "Description",
        "Color",
        "Created At",
        "Deadline",
        "Todo Count",
      ];
      const projectRows = projects.map(project => [
        project.id,
        `"${(project.name || "").replace(/"/g, '""')}"`,
        `"${(project.description || "").replace(/"/g, '""')}"`,
        project.color,
        project.created_at,
        project.deadline || "",
        project.todos.length,
      ]);

      // Combine into a single CSV with sections
      const csvContent = [
        "# Flowcus Data Export",
        `# Generated on: ${new Date().toISOString()}`,
        "",
        "## Todos",
        todoHeaders.join(","),
        ...todoRows.map(row => row.join(",")),
        "",
        "## Projects",
        projectHeaders.join(","),
        ...projectRows.map(row => row.join(",")),
      ].join("\n");

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      const timestamp = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
      link.download = `flowcus-data-export-${timestamp}.csv`;
      link.href = URL.createObjectURL(blob);

      // Trigger download
      link.click();

      // Clean up the object URL
      URL.revokeObjectURL(link.href);

      console.log(`Successfully exported ${todos.length} todos and ${projects.length} projects`);
    } catch (error) {
      console.error("Error exporting data:", error);
      alert(t("settings:export.failed"));
    }
  };

  const handleClearCache = () => {
    if (window.confirm(t("settings:privacy_data.clear_confirmation"))) {
      localStorage.clear();
      sessionStorage.clear();
      // Refresh the page to reflect cleared state
      window.location.reload();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      // Clear local storage on logout
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const appVersion = packageInfo.version;

  return (
    <div className={styles.settingsPage}>
      <div className={styles.header}>
        <button onClick={onNavigateBack} className={styles.backButton} aria-label="Go back">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1>{t("settings:title")}</h1>
      </div>

      <div className={styles.content}>
        {/* General Settings */}
        <section className={styles.settingsSection}>
          <h2>{t("settings:sections.general")}</h2>

          {/* Language Selection */}
          <div className={styles.settingItem}>
            <div className={styles.settingSelect}>
              <span>{t("settings:language.description")}</span>
              <select value={currentLanguage} onChange={e => handleLanguageChange(e.target.value)}>
                {supportedLanguages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {t(`settings:language.${lang.code}`, { defaultValue: lang.nativeName })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Account */}
        <section className={styles.settingsSection}>
          <h2>{t("settings:sections.account")}</h2>
          <div className={styles.settingItem}>
            <button className={styles.settingButton} onClick={handleLogout}>
              <span className="material-symbols-outlined">logout</span>
              {t("settings:account.logout")}
            </button>
          </div>
        </section>

        {/* Privacy & Data */}
        <section className={styles.settingsSection}>
          <h2>{t("settings:sections.privacy_data")}</h2>
          <div className={styles.settingItem}>
            <button className={styles.settingButton} onClick={handleExportData}>
              <span className="material-symbols-outlined">download</span>
              {t("settings:privacy_data.export_data")}
            </button>
          </div>
          <div className={styles.settingItem}>
            <button className={styles.settingButton} onClick={handleClearCache}>
              <span className="material-symbols-outlined">delete</span>
              {t("settings:privacy_data.clear_cache")}
            </button>
          </div>
          <div className={styles.settingItem}>
            <a
              href="https://flowcus.work/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.settingLink}
            >
              <span className="material-symbols-outlined">privacy_tip</span>
              {t("settings:privacy_data.privacy_policy")}
              <span className="material-symbols-outlined">open_in_new</span>
            </a>
          </div>
        </section>

        {/* Notifications */}
        <section className={styles.settingsSection}>
          <h2>{t("settings:sections.notifications")}</h2>

          {/* Platform Support Info */}
          {!platformInfo.supportsNotifications && (
            <div className={styles.settingItem}>
              <div className={styles.settingWarning}>
                <span className="material-symbols-outlined">warning</span>
                {t("settings:notifications.not_supported")}
              </div>
            </div>
          )}

          {/* Notification Permission */}
          <div className={styles.settingItem}>
            <div className={styles.settingWithButton}>
              <div className={styles.settingInfo}>
                <span>{t("settings:notifications.push_notifications")}</span>
                <small>{notificationService.getStatusMessage()}</small>
              </div>
              {platformInfo.supportsNotifications && (
                <button
                  className={`${styles.settingButton} ${notificationPermission === "granted" ? styles.enabled : ""}`}
                  onClick={handleNotificationToggle}
                  disabled={notificationPermission === "denied"}
                >
                  {notificationPermission === "granted"
                    ? t("settings:notifications.status.enabled")
                    : notificationPermission === "denied"
                      ? t("settings:notifications.status.blocked")
                      : t("settings:notifications.status.enable")}
                </button>
              )}
            </div>
          </div>

          {/* Test Notification */}
          {notificationPermission === "granted" && (
            <div className={styles.settingItem}>
              <button className={styles.settingButton} onClick={handleTestNotification}>
                <span className="material-symbols-outlined">notifications</span>
                {t("settings:notifications.test_notification")}
              </button>
            </div>
          )}

          {/* Notification Sound */}
          <div className={styles.settingItem}>
            <label className={styles.settingToggle}>
              <span>{t("settings:notifications.notification_sound")}</span>
              <input
                type="checkbox"
                checked={notificationSound}
                onChange={e => handleNotificationSoundChange(e.target.checked)}
                disabled={notificationPermission !== "granted"}
              />
              <span className={styles.toggleSwitch}></span>
            </label>
          </div>

          {/* Platform-specific instructions */}
          {platformInfo.supportsNotifications && (
            <div className={styles.settingItem}>
              <details className={styles.settingDetails}>
                <summary>{t("settings:notifications.platform_instructions")}</summary>
                <div className={styles.instructionsList}>
                  {notificationService
                    .getBrowserSpecificInstructions()
                    .map((instruction, index) => (
                      <p key={index}>{instruction}</p>
                    ))}
                </div>
              </details>
            </div>
          )}
        </section>

        {/* Accessibility */}
        <section className={styles.settingsSection}>
          <h2>{t("settings:sections.accessibility")}</h2>
          <div className={styles.settingItem}>
            <label className={styles.settingSelect}>
              <span>{t("settings:accessibility.text_size")}</span>
              <select value={textSize} onChange={e => handleTextSizeChange(e.target.value)}>
                <option value="small">{t("settings:accessibility.text_sizes.small")}</option>
                <option value="medium">{t("settings:accessibility.text_sizes.medium")}</option>
                <option value="large">{t("settings:accessibility.text_sizes.large")}</option>
                <option value="extra-large">
                  {t("settings:accessibility.text_sizes.extra-large")}
                </option>
              </select>
            </label>
          </div>
          <div className={styles.settingItem}>
            <label className={styles.settingToggle}>
              <span>{t("settings:accessibility.high_contrast")}</span>
              <input
                type="checkbox"
                checked={highContrast}
                onChange={e => handleHighContrastChange(e.target.checked)}
              />
              <span className={styles.toggleSwitch}></span>
            </label>
          </div>
          <div className={styles.settingItem}>
            <label className={styles.settingToggle}>
              <span>{t("settings:accessibility.screen_reader")}</span>
              <input
                type="checkbox"
                checked={screenReader}
                onChange={e => handleScreenReaderChange(e.target.checked)}
              />
              <span className={styles.toggleSwitch}></span>
            </label>
          </div>
        </section>

        {/* Support */}
        <section className={styles.settingsSection}>
          <h2>{t("settings:sections.support")}</h2>
          <div className={styles.settingItem}>
            <a
              href="https://flowcus.work/faq"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.settingLink}
            >
              <span className="material-symbols-outlined">help</span>
              {t("settings:support.faq")}
              <span className="material-symbols-outlined">open_in_new</span>
            </a>
          </div>
          <div className={styles.settingItem}>
            <a href="mailto:helpme@flowcus.work" className={styles.settingLink}>
              <span className="material-symbols-outlined">contact_support</span>
              {t("settings:support.contact_support")}
              <span className="material-symbols-outlined">open_in_new</span>
            </a>
          </div>
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <span className="material-symbols-outlined">info</span>
              {t("settings:support.app_version")}: {appVersion}
            </div>
          </div>
        </section>

        {/* Legal */}
        <section className={styles.settingsSection}>
          <h2>{t("settings:sections.legal")}</h2>
          <div className={styles.settingItem}>
            <a
              href="https://flowcus.work/terms"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.settingLink}
            >
              <span className="material-symbols-outlined">gavel</span>
              {t("settings:legal.terms_of_service")}
              <span className="material-symbols-outlined">open_in_new</span>
            </a>
          </div>
          <div className={styles.settingItem}>
            <a
              href="https://flowcus.work/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.settingLink}
            >
              <span className="material-symbols-outlined">privacy_tip</span>
              {t("settings:legal.privacy_policy")}
              <span className="material-symbols-outlined">open_in_new</span>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SettingsPage;
